import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/utils/apiErrors';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { assignReferee, getClockingPresence } from '@/api/TournamentManagement/refereeApi';
import { isPlayedMatchStatus } from '@/utils/tournamentPostCompletion';
import { MatchRole, resolveEnum, RefereeCertification } from '@/models/tournament';
import type { MatchView, RefereeView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';
import { appDayMonth, appTime } from '@/utils/appDateFormat';
import ClawbackConsentDialog from '../../shared/ClawbackConsentDialog';
import { useClawbackConsent } from '../../shared/useClawbackConsent';

interface ConfirmAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  match: MatchView | null;
  referee: RefereeView | null;
  assignmentType: 'new' | 'reassignment';
  /**
   * CO-921: the viewer holds the Hr role. The clock-in hint endpoint is HR only, and the page never
   * opens this dialog for a non-HR user on a played match (the server would refuse with a 403).
   */
  isHr?: boolean;
}

/** CO-921: clock-in hint state for the two officials of a replacement on a played match. */
type ClockingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; byUser: Record<string, boolean> };

const ConfirmAssignmentModal: React.FC<ConfirmAssignmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  match,
  referee,
  assignmentType,
  isHr,
}) => {
  const { t } = useTranslation('tournament');
  const [notifyParties, setNotifyParties] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [workPointOverride, setWorkPointOverride] = useState<string>('');
  const [replaceUserId, setReplaceUserId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // CO-921/CO-922 Round 2: replacing or re-roling an official on a paid match takes their pay back;
  // the server refuses until HR approves the exact amounts.
  const clawback = useClawbackConsent();

  const canConfirm = selectedRole !== '';

  // The replace target belongs to ONE role. Changing the role hides the picker but used to keep the
  // stale userId, and the server matches ReplaceUserId by user alone (no role check) — so confirming
  // archived a referee in a role the HR user was no longer looking at. Clear it with the role.
  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    setReplaceUserId('');
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const mapMatchRole = (value: string): MatchRole => {
    const num = parseInt(value, 10);
    if (num === MatchRole.AssistantReferee) return MatchRole.AssistantReferee;
    if (num === MatchRole.FourthOfficial) return MatchRole.FourthOfficial;
    if (num === MatchRole.Commissioner) return MatchRole.Commissioner;
    return MatchRole.HeadReferee;
  };

  const selectedRoleValue = selectedRole === '' ? null : mapMatchRole(selectedRole);
  const sameRoleMembers = (match?.refereeTeamMembers ?? []).filter(
    (m) => selectedRoleValue != null
      && ((resolveEnum(MatchRole, m.role) as MatchRole) ?? m.role) === selectedRoleValue
      && m.userId !== referee?.userId,
  );
  // Only ever send a replace target the picker is currently offering.
  const effectiveReplaceUserId = sameRoleMembers.some((m) => m.userId === replaceUserId)
    ? replaceUserId
    : '';

  // CO-921 (owner 2026-10-21): on a PLAYED match (Completed / UnderRevision) a replacement moves the
  // match's work points from the replaced official to the new one, and anything already paid to the
  // replaced official is reversed on the next payroll. Adding alongside prices the new official now.
  const isPlayed = !!match && isPlayedMatchStatus(match.status);
  const replacedMember = sameRoleMembers.find((m) => m.userId === effectiveReplaceUserId) ?? null;

  // Read-only hint: who has a clock-in in this match window. Clock-ins never move (owner decision 3);
  // only the scheduled shift follows the new official, so HR should see who actually clocked in.
  const [clocking, setClocking] = useState<ClockingState>({ status: 'idle' });
  const replacedUserId = replacedMember?.userId ?? '';
  const newUserId = referee?.userId ?? '';
  const matchId = match?.id;
  useEffect(() => {
    if (!isOpen || !isPlayed || !isHr || !matchId || !replacedUserId || !newUserId) {
      setClocking({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setClocking({ status: 'loading' });
    getClockingPresence(matchId, [replacedUserId, newUserId])
      .then((result) => {
        if (cancelled) return;
        if (result.errors.length > 0 || !result.payload) {
          setClocking({ status: 'error' });
          return;
        }
        const byUser: Record<string, boolean> = {};
        for (const row of result.payload) byUser[row.userId] = row.hasClockIn;
        setClocking({ status: 'ready', byUser });
      })
      .catch(() => { if (!cancelled) setClocking({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [isOpen, isPlayed, isHr, matchId, replacedUserId, newUserId]);

  const handleConfirm = async () => {
    if (!canConfirm || !match || !referee) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const outcome = await clawback.run((consent) => assignReferee({
        matchId: match.id,
        refereeProfileId: referee.id,
        role: mapMatchRole(selectedRole),
        notifyParties,
        reason: effectiveReplaceUserId
          ? 'Replacement via assignment center'
          : assignmentType === 'reassignment' ? 'Reassignment via assignment center' : undefined,
        workPointOverride: workPointOverride ? Number(workPointOverride) : undefined,
        // Wired to RefereeService.AssignReferee(ReplaceUserId): the replaced referee's assignment is
        // archived and they receive the unassignment notification.
        replaceUserId: effectiveReplaceUserId || undefined,
      }, { clawbackConsent: consent }));

      // Declined: this dialog stays open with the same selection. Not HR: say why; nothing changed.
      if (outcome.status === 'declined') return;
      if (outcome.status === 'requiresHr') {
        setApiError(outcome.message);
        return;
      }
      const result = outcome.result;
      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to assign official'));
        return;
      }

      onConfirm();
      handleClose();
    } catch (error: unknown) {
      // A 400 refusal (e.g. an overlapping referee booking) is a rejected promise; the shared helper reads
      // every error shape the request layer produces (including the interceptor's stamped errors).
      setApiError(getApiErrorMessage(error, t, t('An unexpected error occurred. Please try again.'), ' '));
      console.error('Error confirming assignment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNotifyParties(true);
    setSelectedRole('');
    setWorkPointOverride('');
    setReplaceUserId('');
    setApiError(null);
    onClose();
  };

  if (!isOpen || !match || !referee) return null;

  const formatDate = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return appDayMonth(date);
  };

  const formatTime = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return appTime(date);
  };

  const certificationKeys: Record<number, string> = {
    0: 'National Level A',
    1: 'AFC Elite',
    2: 'FIFA International',
  };

  return (
    <>
    {/* Was a hand-rolled `fixed inset-0` overlay: no role="dialog", no aria-modal, no Escape, no
       focus trap, no focus restore, and an icon-only close button with no accessible name. This is
       now the repo's Radix Dialog, which supplies all of that (and names its own close button).
       Content MUST live inside <DialogBody> — direct children of DialogContent get zero padding. */}
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) handleClose();
      }}
    >
      <DialogContent
        size="lg"
        // Every dismissal path is locked together while the assignment is in flight.
        onEscapeKeyDown={(e) => { if (isSubmitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        data-testid="confirm-assignment-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="AlertTriangle" size={24} className="me-2 text-amber-600 dark:text-amber-400" />
            {t('Confirm Official Assignment')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-6">
          {/* API Error */}
          {apiError && (
            <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
              </div>
            </div>
          )}

          {/* Assignment Summary. The `from-primary/5 to-blue-50` wash leaned on the exact primary
              hue, which every tenant can override at runtime. */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <p className="text-sm font-medium text-foreground mb-3">{t('Assignment Summary')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Match */}
              <div>
                <div className="flex items-center mb-1.5">
                  <Icon name="Calendar" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Match')}</p>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {match.team1Name} {t('vs')} {match.team2Name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  #{match.id}
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  {t('{{date}} at {{time}}', {
                    date: formatDate(match.scheduledDateTime),
                    time: formatTime(match.scheduledDateTime),
                  })}
                </p>
              </div>

              {/* Referee */}
              <div className="sm:border-s sm:border-border sm:ps-4">
                <div className="flex items-center mb-1.5">
                  <Icon name="User" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Official')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {referee.photoUrl ? (
                    <img
                      src={referee.photoUrl}
                      alt={t('{{name}} profile photo', { name: referee.name })}
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      <Icon name="User" size={18} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{referee.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t(certificationKeys[(resolveEnum(RefereeCertification, referee.certification) as RefereeCertification)] ?? 'Unknown')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Match Role Selection — the label now comes from ui/Select, which wires htmlFor/id and
              names the combobox. It was a loose <p> before, so the control had no accessible name. */}
          <div>
            <Select
              label={t('Select Match Role')}
              required
              value={selectedRole}
              onChange={handleRoleChange}
              options={[
                { value: '', label: t('Select Role') },
                { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                { value: String(MatchRole.Commissioner), label: t('Commissioner') },
              ]}
              placeholder={t('Select Role')}
              data-testid="confirm-assignment-role-select"
            />
            {!selectedRole && (
              <p className="text-xs text-muted-foreground mt-1.5">{t('A match role is required to proceed.')}</p>
            )}
          </div>

          {/* Replace an existing referee in the chosen role (optional) */}
          {selectedRole && sameRoleMembers.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-testid="confirm-assignment-replace-section">
              <Select
                label={t('Replace an existing official?')}
                value={replaceUserId}
                onChange={setReplaceUserId}
                options={[
                  { value: '', label: t('Add alongside (no replacement)') },
                  ...sameRoleMembers.map((m) => ({ value: m.userId, label: m.userName })),
                ]}
                data-testid="confirm-assignment-replace-select"
              />
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-2">
                {t('Pick who this official takes over from. Their assignment is removed and they are notified. Leave empty to add alongside them.')}
              </p>
            </div>
          )}

          {/* CO-921: what this assignment does to pay on a played match. */}
          {isPlayed && selectedRole && (
            <div
              role="status"
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20"
              data-testid="confirm-assignment-points-notice"
            >
              <div className="flex items-start gap-3">
                <Icon name="Coins" size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{t('wpFollow.assign.title')}</p>
                  {replacedMember ? (
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      {(replacedMember.workPoints ?? 0) > 0
                        ? t('wpFollow.assign.replaceBody', { points: replacedMember.workPoints, from: replacedMember.userName, to: referee.name })
                        : t('wpFollow.assign.replaceBodyNoPoints', { from: replacedMember.userName, to: referee.name })}
                    </p>
                  ) : (
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      {t('wpFollow.assign.addBody', { name: referee.name })}
                    </p>
                  )}
                  {replacedMember && (
                    <div className="border-t border-amber-200 pt-2 dark:border-amber-800" data-testid="confirm-assignment-clocking-hint">
                      <p className="mb-1 text-xs font-medium text-amber-900 dark:text-amber-200">{t('wpFollow.clocking.title')}</p>
                      {clocking.status === 'loading' && (
                        <p className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                          <Icon name="Loader2" size={12} className="animate-spin" />
                          {t('wpFollow.clocking.loading')}
                        </p>
                      )}
                      {clocking.status === 'error' && (
                        <p className="text-xs text-amber-800 dark:text-amber-300">{t('wpFollow.clocking.unavailable')}</p>
                      )}
                      {clocking.status === 'ready' && (
                        <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
                          {[{ id: replacedMember.userId, name: replacedMember.userName }, { id: referee.userId, name: referee.name }].map((p) => (
                            <li key={p.id} className="flex items-center gap-1.5" data-testid={`confirm-assignment-clocking-${p.id}`}>
                              <Icon name={clocking.byUser[p.id] ? 'Clock' : 'MinusCircle'} size={12} className="shrink-0" />
                              {clocking.byUser[p.id]
                                ? t('wpFollow.clocking.has', { name: p.name })
                                : t('wpFollow.clocking.none', { name: p.name })}
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">{t('wpFollow.clocking.note')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Work Point Override — this input had no accessible name at all: a loose <p> above it and
              no label/id. It sets a referee's PAY for the match. */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <Input
              id="confirm-assignment-workpoint-override"
              type="number"
              label={t('Work Point Override')}
              description={t('Leave empty to use automatic calculation based on work point rules.')}
              value={workPointOverride}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkPointOverride(e.target.value)}
              placeholder={t('Auto')}
              min="0"
              className="w-40"
              disabled={isSubmitting}
              data-testid="confirm-assignment-workpoint-override-input"
            />
          </div>

          {/* Existing Referee Team Members */}
          {match.refereeTeamMembers?.length > 0 && (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-2">{t('Existing Match Officials')}</p>
              <div className="space-y-1.5">
                {match.refereeTeamMembers.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Icon name="User" size={14} className="text-muted-foreground me-2" />
                    <span className="font-medium text-foreground">{member.userName}</span>
                    <span className="text-muted-foreground ms-1.5 text-xs">
                      ({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reassignment Warning */}
          {/* Not while replacing: "will be added" contradicts the replacement the HR user just picked. */}
          {assignmentType === 'reassignment' && !effectiveReplaceUserId && (match.refereeTeamMembers?.length ?? 0) > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="UserX" size={18} className="text-amber-600 dark:text-amber-400 me-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">{t('Adding to Existing Team')}</p>
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">
                    {t('{{newName}} will be added to the officials for this match.', { newName: referee.name })}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {t('The official will be notified of this assignment.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Option */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="notify-parties"
                checked={notifyParties}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyParties(e.target.checked)}
                className="mt-1 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
                data-testid="confirm-assignment-notify-checkbox"
              />
              <label htmlFor="notify-parties" className="flex-1 cursor-pointer">
                <p className="text-sm font-medium text-foreground">{t('Send notifications')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Notify the assigned official about this assignment')}
                </p>
              </label>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="bg-muted">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="confirm-assignment-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            loading={isSubmitting}
            iconName={isSubmitting ? null : 'CheckCircle'}
            // CO-921: on a played match this moves pay, so it is the destructive kind and names the outcome.
            variant={isPlayed ? 'destructive' : undefined}
            data-testid="confirm-assignment-submit-btn"
          >
            {isPlayed
              ? (effectiveReplaceUserId ? t('wpFollow.assign.confirmReplace') : t('wpFollow.assign.confirmAdd'))
              : t('Assign Official')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ClawbackConsentDialog {...clawback.dialogProps} />
    </>
  );
};

export default ConfirmAssignmentModal;
